import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { styled } from "styled-components";

interface ViewOption {
  readonly label: string;
  readonly path: string;
}

const VIEWS: readonly ViewOption[] = [
  { label: "Flights", path: "/flights" },
  { label: "Nexus", path: "/nexus" },
];

/**
 * Segmented control that swaps between the Flights and Nexus pages.
 * Visible in all envs — both pages live in prod now that the prod
 * Nexus pipeline is wired up.
 */
const ViewSwitcher = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activePath = VIEWS.find((v) =>
    location.pathname.startsWith(v.path),
  )?.path;

  return (
    <SwitcherRow role="tablist" aria-label="Flights / Nexus view">
      {VIEWS.map((view) => (
        <SwitcherButton
          key={view.path}
          type="button"
          role="tab"
          aria-selected={activePath === view.path}
          $active={activePath === view.path}
          onClick={() => {
            if (activePath !== view.path) navigate(view.path);
          }}
        >
          {view.label}
        </SwitcherButton>
      ))}
    </SwitcherRow>
  );
};

const SwitcherRow = styled.div`
  display: inline-flex;
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.paper};
  border: 1px solid ${(props) => props.theme.colors.lightGrey};
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
  align-self: flex-start;
`;

const SwitcherButton = styled.button<{ $active: boolean }>`
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  cursor: pointer;
  transition:
    background-color 0.15s,
    color 0.15s;
  background-color: ${(props) =>
    props.$active ? props.theme.colors.blue : "transparent"};
  color: ${(props) =>
    props.$active ? props.theme.colors.white : props.theme.colors.grey};

  &:hover {
    color: ${(props) =>
      props.$active ? props.theme.colors.white : props.theme.colors.blue};
  }
`;

export default ViewSwitcher;
